import { Injectable, inject, signal } from '@angular/core';
import { ApiClientService } from '../core/api/api-client.service';
import { OrganizationStore } from '../stores/organization.store';
import { UiStore } from '../stores/ui.store';
import {
  OrganizationModel,
  OrganizationTimingModel,
  GetOrganizationsResponse,
  GetOrganizationResponse,
  GetOrganizationScheduleMappingStatusesResponse,
  OrganizationScheduleMappingStatus,
  StoreOrganizationResponse,
  StoreOrganizationTimingsResponse,
} from '../shared/models/organization.model';
import { ApiResponse } from '../shared/models/types';

@Injectable({ providedIn: 'root' })
export class OrganizationManagerService {
  private readonly apiClient = inject(ApiClientService);
  private readonly organizationStore = inject(OrganizationStore);
  private readonly uiStore = inject(UiStore);
  private readonly baseUrl = 'admin/organizations';

  private readonly _isLoading = signal(false);
  readonly isLoading = this._isLoading.asReadonly();
  readonly organizations = this.organizationStore.organizations;
  readonly selectedOrganization = this.organizationStore.selectedOrganization;
  readonly selectedScheduleMapping = this.organizationStore.selectedScheduleMapping;
  readonly scheduleMappingStatuses = this.organizationStore.scheduleMappingStatuses;

  async loadOrganizations(): Promise<void> {
    this._isLoading.set(true);
    try {
      const result = await this.apiClient.get<GetOrganizationsResponse>(this.baseUrl);

      if (result.success) {
        this.organizationStore.setOrganizations(result.organizations);
      } else {
        this.uiStore.showToast(result.messages?.[0] ?? 'Failed to load organizations.', 'error');
      }
    } finally {
      this._isLoading.set(false);
    }
  }

  async loadOrganization(id: Guid): Promise<void> {
    this.organizationStore.setSelectedScheduleMapping(null);

    const result = await this.apiClient.get<GetOrganizationResponse>(`${this.baseUrl}/${id}`);

    if (result.success) {
      this.organizationStore.setSelected(result.organization);
      this.organizationStore.setSelectedScheduleMapping({
        hasGingrIntegration: result.hasGingrIntegration,
        organizationTimings: result.organizationTimings,
        gingrTimingOptions: result.gingrTimingOptions,
        gingrMedicationTimingOptions: result.gingrMedicationTimingOptions,
      });
    } else {
      this.uiStore.showToast(result.messages?.[0] ?? 'Failed to load organization.', 'error');
    }
  }

  async saveOrganization(org: OrganizationModel): Promise<StoreOrganizationResponse> {
    const result = await this.apiClient.post<OrganizationModel, StoreOrganizationResponse>(
      this.baseUrl,
      org
    );

    if (result.success) {
      if (org.id === null) {
        this.organizationStore.addOrganization(result.organization);
      } else {
        this.organizationStore.updateOrganization(result.organization);
      }
      this.uiStore.showToast('Organization saved.', 'success');
    } else {
      this.uiStore.showToast(result.messages?.[0] ?? 'Failed to save organization.', 'error');
    }

    return result;
  }

  /** Replaces the organization's Gingr schedule mappings with this full list; rows left out are removed. Global admins only. */
  async saveOrganizationTimings(organizationId: Guid, timings: OrganizationTimingModel[]): Promise<boolean> {
    const result = await this.apiClient.post<OrganizationTimingModel[], StoreOrganizationTimingsResponse>(
      `${this.baseUrl}/${organizationId}/schedule-mappings`,
      timings
    );

    if (result.success) {
      void this.loadScheduleMappingStatuses();
    } else {
      this.uiStore.showToast(result.messages?.[0] ?? 'Failed to save Gingr schedule mapping.', 'error');
    }

    return result.success;
  }

  async loadScheduleMappingStatuses(): Promise<void> {
    try {
      const result = await this.apiClient.get<GetOrganizationScheduleMappingStatusesResponse>(`${this.baseUrl}/schedule-mapping-statuses`);

      if (result.success) {
        this.organizationStore.setScheduleMappingStatuses(result.statuses);
      }
    } catch {
      this.organizationStore.setScheduleMappingStatuses([]);
    }
  }

  async getScheduleMappingStatus(organizationId: Guid): Promise<OrganizationScheduleMappingStatus | undefined> {
    try {
      const result = await this.apiClient.get<GetOrganizationScheduleMappingStatusesResponse>(`${this.baseUrl}/schedule-mapping-statuses`, { organizationId });
      const status = result.success ? result.statuses[0] : undefined;

      return status;
    } catch {
      return undefined;
    }
  }

  async deleteOrganization(id: Guid): Promise<void> {
    const result = await this.apiClient.delete<ApiResponse>(`${this.baseUrl}/${id}`);

    if (result.success) {
      this.organizationStore.removeOrganization(id);
      void this.loadScheduleMappingStatuses();
      this.uiStore.showToast('Organization deleted.', 'success');
    } else {
      this.uiStore.showToast(result.messages?.[0] ?? 'Failed to delete organization.', 'error');
    }
  }
}
